import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { useStoreSettings, getLocalizedText } from '../contexts/StoreSettingsContext';
import api, { API_URL } from '../lib/api';

// Slides par défaut si aucune slide configurée
const DEFAULT_SLIDES = [
  {
    id: 'default-1',
    image_url: 'https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1920&q=80',
    title: { fr: 'Collection Streetwear', he: 'קולקציית סטריטוור', en: 'Streetwear Collection' },
    subtitle: { fr: 'Les pièces les plus recherchées au meilleur prix', he: 'הפריטים המבוקשים ביותר במחיר הטוב ביותר', en: 'The most wanted pieces at the best price' },
    cta_text: { fr: 'Découvrir', he: 'גלה', en: 'Discover' },
    cta_url: '/products',
    enabled: true
  },
  {
    id: 'default-2',
    image_url: 'https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=1920&q=80',
    title: { fr: 'Nouveautés', he: 'חדשים', en: 'New Arrivals' },
    subtitle: { fr: 'Découvrez nos dernières arrivées chaque semaine', he: 'גלה את ההגעות החדשות שלנו כל שבוע', en: 'Discover our latest arrivals every week' },
    cta_text: { fr: 'Voir les nouveautés', he: 'צפה בחדשים', en: 'See new arrivals' },
    cta_url: '/products?filter=new',
    enabled: true
  },
  {
    id: 'default-3',
    image_url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1920&q=80',
    title: { fr: 'Sneakers Premium', he: 'סניקרס פרימיום', en: 'Premium Sneakers' },
    subtitle: { fr: 'Nike, Jordan, Yeezy et plus encore', he: 'נייקי, ג׳ורדן, ייזי ועוד', en: 'Nike, Jordan, Yeezy and more' },
    cta_text: { fr: 'Shop Sneakers', he: 'קנה סניקרס', en: 'Shop Sneakers' },
    cta_url: '/category/sneakers',
    enabled: true
  }
];

export function HeroSlider() {
  const { t, i18n } = useTranslation();
  const { settings, loading } = useStoreSettings();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [productPrices, setProductPrices] = useState({}); // Cache des prix dynamiques
  const isRTL = i18n.dir() === 'rtl';
  const lang = i18n.language;
  
  // Ref pour éviter les appels multiples et la boucle infinie
  const pricesFetchedRef = useRef(false);
  const failedProductsRef = useRef(new Set()); // Track les produits 404 pour ne pas re-essayer

  // Récupérer les slides depuis les settings ou utiliser les défauts
  const sliderConfig = settings?.slider || {};
  // Filtrer uniquement les slides activés (enabled: true)
  const allSlides = sliderConfig.slides?.length > 0 ? sliderConfig.slides : DEFAULT_SLIDES;
  const slides = allSlides.filter(slide => slide.enabled !== false);
  const autoplay = sliderConfig.autoplay !== false;
  const autoplaySpeed = sliderConfig.autoplay_speed || 5000;
  const showDots = sliderConfig.show_dots !== false;
  const showArrows = sliderConfig.show_arrows !== false;
  
  // Stabiliser les IDs de produits pour éviter les re-renders inutiles
  const productIdsKey = useMemo(() => {
    return slides
      .filter(s => s.source_product_id)
      .map(s => s.source_product_id)
      .sort()
      .join(',');
  }, [slides]);
  
  // Récupérer les prix actuels des produits associés aux slides - UNE SEULE FOIS
  useEffect(() => {
    // Éviter la boucle infinie : ne fetch qu'une seule fois par combinaison d'IDs
    if (pricesFetchedRef.current || loading || !productIdsKey) return;
    
    const fetchProductPrices = async () => {
      pricesFetchedRef.current = true; // Marquer comme déjà fetchté AVANT l'appel
      
      const productIds = productIdsKey.split(',').filter(id => id && !failedProductsRef.current.has(id));
      
      if (productIds.length === 0) return;
      
      try {
        const priceMap = {};
        const uniqueIds = [...new Set(productIds)];
        
        const results = await Promise.allSettled(
          uniqueIds.map(productId => 
            api.get(`/api/storefront/product-by-id/${productId}`, {
              timeout: 5000
            })
          )
        );
        
        results.forEach((result, index) => {
          const productId = uniqueIds[index];
          
          // Vérifier si le produit existe (non null) et a des données valides
          if (result.status === 'fulfilled' && result.value?.data && result.value.data !== null) {
            const product = result.value.data;
            
            // Double vérification que c'est bien un objet produit
            if (typeof product === 'object' && (product.pricing || product.price)) {
              let price = null;
              if (product.pricing?.prices) {
                const currency = settings?.general?.default_currency || 'ILS';
                price = product.pricing.prices[currency];
              } else if (typeof product.price === 'object') {
                price = product.price.amount;
              } else if (typeof product.price === 'number') {
                price = product.price;
              }
            
            if (price) {
              const currency = settings?.general?.default_currency || 'ILS';
              const symbol = currency === 'ILS' ? '₪' : currency === 'EUR' ? '€' : '$';
              priceMap[productId] = `${symbol}${price.toFixed(0)}`;
            }
            } else {
              // Produit null retourné = non trouvé, ajouter à failedProducts
              failedProductsRef.current.add(productId);
            }
          } else {
            // Produit 404 ou erreur - l'ajouter à la liste des échecs pour ne JAMAIS réessayer
            failedProductsRef.current.add(productId);
            console.warn(`Product ${productId} not found, skipping future requests`);
          }
        });
        
        setProductPrices(priceMap);
      } catch (err) {
        console.warn('Could not fetch dynamic prices for slider');
      }
    };
    
    fetchProductPrices();
  }, [productIdsKey, loading]); // Dépendances stables uniquement
  
  // Reset le ref si les IDs changent vraiment (nouveau slide ajouté par exemple)
  useEffect(() => {
    pricesFetchedRef.current = false;
  }, [productIdsKey]);
  
  // Couleurs personnalisées
  const titleColor = sliderConfig.title_color || '#FFFFFF';
  const subtitleColor = sliderConfig.subtitle_color || '#D1D5DB';
  const ctaTextColor = sliderConfig.cta_text_color || '#000000';
  const ctaBgColor = sliderConfig.cta_bg_color || '#FFFFFF';
  const titleFont = sliderConfig.title_font || 'Oswald';
  const ctaFont = sliderConfig.cta_font || 'Inter';
  const titleFontSize = sliderConfig.title_font_size || '48';
  const imageZoom = sliderConfig.image_zoom || 100;
  
  // Effets de texte
  const titleEffect = sliderConfig.title_effect || 'none';
  const subtitleEffect = sliderConfig.subtitle_effect || 'none';
  
  // Générer les styles d'effet pour le titre
  const getTitleEffectStyle = () => {
    const baseStyle = {};
    switch (titleEffect) {
      case 'shadow':
        baseStyle.textShadow = '3px 3px 6px rgba(0,0,0,0.7), 1px 1px 3px rgba(0,0,0,0.5)';
        break;
      case 'glow':
        baseStyle.textShadow = `0 0 10px ${titleColor}80, 0 0 20px ${titleColor}60, 0 0 30px ${titleColor}40`;
        break;
      case 'outline':
        baseStyle.WebkitTextStroke = `2px ${titleColor}`;
        baseStyle.paintOrder = 'stroke fill';
        break;
      case 'gradient':
        baseStyle.background = 'linear-gradient(90deg, #ff0066, #00ccff)';
        baseStyle.WebkitBackgroundClip = 'text';
        baseStyle.WebkitTextFillColor = 'transparent';
        baseStyle.backgroundClip = 'text';
        break;
      case 'neon':
        baseStyle.textShadow = `0 0 5px ${titleColor}, 0 0 10px ${titleColor}, 0 0 20px #ff0066, 0 0 40px #ff0066`;
        break;
      default:
        // Ombre par défaut pour la lisibilité
        baseStyle.textShadow = '2px 2px 8px rgba(0,0,0,0.8)';
    }
    return baseStyle;
  };
  
  // Générer les styles d'effet pour le sous-titre
  const getSubtitleEffectStyle = () => {
    const baseStyle = {};
    switch (subtitleEffect) {
      case 'shadow':
        baseStyle.textShadow = '2px 2px 4px rgba(0,0,0,0.7)';
        break;
      case 'glow':
        baseStyle.textShadow = `0 0 8px ${subtitleColor}80, 0 0 16px ${subtitleColor}40`;
        break;
      case 'outline':
        baseStyle.WebkitTextStroke = `1px ${subtitleColor}`;
        break;
      case 'neon':
        baseStyle.textShadow = `0 0 5px ${subtitleColor}, 0 0 10px #00ccff`;
        break;
      default:
        // Ombre légère par défaut
        baseStyle.textShadow = '1px 1px 4px rgba(0,0,0,0.6)';
    }
    return baseStyle;
  };

  // Charger les polices Google Fonts dynamiquement
  useEffect(() => {
    const fonts = [
      'Oswald:wght@400;600;700',
      'Space+Grotesk:wght@400;500;600;700',
      'Playfair+Display:wght@400;600;700',
      'Bebas+Neue',
      'Inter:wght@400;500;600;700',
      'Poppins:wght@400;500;600;700',
      'Montserrat:wght@400;500;600;700',
      'Anton',
      'DM+Serif+Display',
      'Archivo+Black',
      'Syne:wght@400;500;600;700',
      'Outfit:wght@400;500;600;700',
      'Plus+Jakarta+Sans:wght@400;500;600;700',
      'Cormorant+Garamond:wght@400;600;700',
      'Work+Sans:wght@400;500;600;700',
      'Roboto:wght@400;500;700'
    ];
    
    const existingLink = document.querySelector('link[data-fonts="hero-slider"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fonts.join('&family=')}&display=swap`;
      link.setAttribute('data-fonts', 'hero-slider');
      document.head.appendChild(link);
    }
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // Auto-advance slides
  useEffect(() => {
    if (isPaused || !autoplay || slides.length <= 1) return;
    const timer = setInterval(nextSlide, autoplaySpeed);
    return () => clearInterval(timer);
  }, [isPaused, autoplay, autoplaySpeed, nextSlide, slides.length]);

  // Reset current slide si les slides changent
  useEffect(() => {
    if (currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  // Obtenir l'URL de l'image (desktop)
  const getImageUrl = (slide) => {
    // Image uploadée via API
    if (slide.image_url?.startsWith('/api/')) {
      return `${API_URL}${slide.image_url}`;
    }
    // Image base64
    if (slide.image_base64) {
      return slide.image_base64;
    }
    // URL externe
    return slide.image_url || 'https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1920&q=80';
  };

  // Obtenir l'URL de l'image mobile
  const getMobileImageUrl = (slide) => {
    // Image mobile uploadée via API
    if (slide.image_mobile_url?.startsWith('/api/')) {
      return `${API_URL}${slide.image_mobile_url}`;
    }
    // Image mobile base64
    if (slide.image_mobile_base64) {
      return slide.image_mobile_base64;
    }
    // Pas d'image mobile, retourner null
    return null;
  };
  
  // État pour détecter si on est sur mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (loading) {
    return (
      <section 
        className="relative w-full h-[280px] sm:h-[320px] md:h-[55vh] bg-mutagim-dark flex items-center justify-center -mt-px"
        data-testid="hero-slider"
      >
        <div className="animate-pulse w-16 h-16 border-4 border-mutagim-red border-t-transparent rounded-full" />
      </section>
    );
  }

  return (
    <section 
      className="relative w-full h-[280px] sm:h-[320px] md:h-[55vh] -mt-px overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="hero-slider"
      style={{ backgroundColor: '#000' }}
    >
      {/* Slides */}
      {slides.map((slide, index) => {
        // Choisir l'image selon la taille d'écran
        const mobileImg = getMobileImageUrl(slide);
        const desktopImg = getImageUrl(slide);
        const currentImg = (isMobile && mobileImg) ? mobileImg : desktopImg;
        
        return (
        <div
          key={slide.id}
          className={cn(
            "absolute inset-0 transition-opacity duration-700",
            index === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          {/* Fond flouté qui couvre TOUT l'écran y compris les côtés */}
          <div 
            className="absolute inset-0"
            style={{ 
              backgroundImage: `url(${desktopImg})`,
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              filter: 'blur(50px) brightness(0.6) saturate(1.2)',
            }}
          />
          
          {/* Image principale - centrée */}
          <div 
            className="absolute inset-0"
            style={{ 
              backgroundImage: `url(${currentImg})`,
              backgroundPosition: isMobile && mobileImg 
                ? 'center center' 
                : `center ${slide.image_position_y || 50}%`,
              backgroundSize: isMobile && mobileImg 
                ? 'cover'
                : imageZoom <= 50 
                  ? 'contain' 
                  : imageZoom >= 100 
                    ? 'cover'
                    : `${imageZoom + 30}%`,
              backgroundRepeat: 'no-repeat'
            }}
          />
          
          {/* Overlay sombre configurable par slide */}
          {(slide.overlay_opacity > 0) && (
            <div 
              className="absolute inset-0"
              style={{
                backgroundColor: `rgba(0, 0, 0, ${(slide.overlay_opacity || 0) / 100})`
              }}
            />
          )}
          
          {/* Gradient vertical (bas vers noir) */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgb(10, 10, 10) 0%, rgba(10, 10, 10, 0.6) 20%, transparent 50%)'
            }}
          />
          
          {/* Gradient vertical (haut subtil) */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(10, 10, 10, 0.4) 0%, transparent 30%)'
            }}
          />
          
          {/* Vignette sur les côtés - fondu progressif vers le noir */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                linear-gradient(90deg, 
                  rgba(10, 10, 10, 0.7) 0%, 
                  rgba(10, 10, 10, 0.3) 10%, 
                  transparent 25%, 
                  transparent 75%, 
                  rgba(10, 10, 10, 0.3) 90%, 
                  rgba(10, 10, 10, 0.7) 100%
                )
              `
            }}
          />

          {/* Content */}
          <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
            <div className={cn(
              "max-w-xl space-y-3 md:space-y-4",
              isRTL ? "text-right ms-auto" : "text-left"
            )}>
              <h1 
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold font-heading animate-fade-in leading-tight"
                style={{ 
                  color: titleEffect === 'gradient' ? undefined : titleColor,
                  fontFamily: titleFont,
                  fontSize: isMobile ? undefined : `${titleFontSize}px`,
                  ...getTitleEffectStyle()
                }}
              >
                {getLocalizedText(slide.title, lang)}
              </h1>
              {slide.subtitle && (
                <p 
                  className="text-sm sm:text-base md:text-lg lg:text-xl max-w-md"
                  style={{ 
                    color: subtitleColor,
                    ...getSubtitleEffectStyle()
                  }}
                >
                  {getLocalizedText(slide.subtitle, lang)}
                </p>
              )}
              {slide.cta_text && slide.cta_url && (
                <Link to={slide.cta_url}>
                  <button 
                    className="px-4 py-2 md:px-6 md:py-3 rounded font-semibold mt-2 md:mt-4 transition-all hover:opacity-90 hover:scale-105 text-sm md:text-base"
                    style={{ 
                      backgroundColor: ctaBgColor,
                      color: ctaTextColor,
                      fontFamily: ctaFont,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.3)'
                    }}
                    data-testid="hero-cta"
                  >
                    {getLocalizedText(slide.cta_text, lang)}
                  </button>
                </Link>
              )}
            </div>
          </div>
          
          {/* Vignette produit source - visible si le slide a un produit associé */}
          {slide.source_product_id && slide.source_product_image && (
            <Link 
              to={slide.source_product_slug ? `/product/${slide.source_product_slug}` : `/product/${slide.source_product_id}`}
              className={cn(
                "absolute bottom-4 md:bottom-8 z-20 group",
                isRTL ? "left-4 md:left-16 lg:left-24" : "right-4 md:right-16 lg:right-24"
              )}
              data-testid="hero-product-thumbnail"
            >
              <div className="relative bg-white rounded-xl md:rounded-2xl p-2 md:p-4 border-2 border-gray-200 shadow-xl transition-all duration-300 hover:scale-105 hover:border-red-500 hover:shadow-[0_0_30px_rgba(192,0,12,0.5)] group-hover:shadow-[0_0_40px_rgba(192,0,12,0.6)]">
                {/* Image produit - format horizontal/vignette */}
                <img 
                  src={slide.source_product_image.startsWith('/api/') 
                    ? `${API_URL}${slide.source_product_image}` 
                    : slide.source_product_image
                  }
                  alt={slide.source_product_name || 'Produit'}
                  className="w-24 h-16 sm:w-36 sm:h-24 md:w-48 md:h-32 lg:w-56 lg:h-36 object-cover rounded-lg transition-all duration-300 group-hover:brightness-110"
                />
                
                {/* Infos produit sous l'image */}
                <div className="mt-2 md:mt-3 text-center">
                  {slide.source_product_name && (
                    <p className="text-gray-800 text-xs sm:text-sm md:text-base font-medium truncate max-w-[96px] sm:max-w-[144px] md:max-w-[192px] lg:max-w-[224px]">
                      {slide.source_product_name}
                    </p>
                  )}
                  {(productPrices[slide.source_product_id] || slide.source_product_price) && (
                    <p className="text-gray-900 font-bold text-sm md:text-lg mt-1">
                      {productPrices[slide.source_product_id] || slide.source_product_price}
                    </p>
                  )}
                </div>
                
                {/* Badge "Voir" */}
                <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 bg-red-600 text-white text-[10px] md:text-sm font-bold px-2 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg">
                  Voir →
                </div>
              </div>
            </Link>
          )}
        </div>
      );
      })}

      {/* Navigation arrows */}
      {showArrows && slides.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "absolute top-1/2 -translate-y-1/2 text-white bg-black/30 hover:bg-black/50 rounded-full",
              isRTL ? "end-4" : "start-4"
            )}
            onClick={prevSlide}
            data-testid="hero-prev"
          >
            {isRTL ? <ChevronRight className="h-6 w-6" /> : <ChevronLeft className="h-6 w-6" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "absolute top-1/2 -translate-y-1/2 text-white bg-black/30 hover:bg-black/50 rounded-full",
              isRTL ? "start-4" : "end-4"
            )}
            onClick={nextSlide}
            data-testid="hero-next"
          >
            {isRTL ? <ChevronLeft className="h-6 w-6" /> : <ChevronRight className="h-6 w-6" />}
          </Button>
        </>
      )}

      {/* Dots indicator - désactivé */}
    </section>
  );
}

export default HeroSlider;
